import React, { useState, useRef, useEffect } from 'react'
import { HiChartBar, HiHome, HiArrowLeft, HiLogout, HiUser, HiViewGrid } from 'react-icons/hi'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import useAuth from '../hooks/useAuth'
import AuthModal from './AuthModal'
import './Header.css'

function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, isAuthenticated, signOut, initializing } = useAuth()

  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authModalTab, setAuthModalTab] = useState('login')
  const [showUserMenu, setShowUserMenu] = useState(false)

  const userMenuRef = useRef(null)

  const isPollPage = location.pathname.startsWith('/poll/')

  // Check if redirected with showAuth state (from ProtectedRoute)
  useEffect(() => {
    if (location.state?.showAuth) {
      setShowAuthModal(true)
      setAuthModalTab('login')
      // Clear the state to prevent modal re-showing on navigation
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, navigate, location.pathname])

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLoginClick = () => {
    setAuthModalTab('login')
    setShowAuthModal(true)
  }

  const handleSignUpClick = () => {
    setAuthModalTab('signup')
    setShowAuthModal(true)
  }

  const handleSignOut = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowUserMenu(false)
    await signOut()
  }

  // Get display name or fallback
  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'User'
  const avatarLetter = displayName.charAt(0).toUpperCase()

  return (
    <>
      <header className="header">
        <div className="container flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="header-logo gradient-text"
          >
            <HiChartBar style={{ fontSize: '1.75rem' }} />
            Realtime Polls
          </div>
          <nav className="nav-menu">
            {isPollPage && (
              <Link
                to="/"
                className="nav-link"
                aria-label="Back"
                title="Back to Home"
              >
                <HiArrowLeft />
                <span>Back</span>
              </Link>
            )}
            <Link
              to="/"
              className="nav-link"
            >
              <HiHome />
              <span>Home</span>
            </Link>
            <Link
              to="/dashboard"
              className="nav-link"
            >
              <HiViewGrid />
              <span>Dashboard</span>
            </Link>

            {/* Auth section */}
            {isAuthenticated ? (
              <div className="user-menu-container" ref={userMenuRef}>
                <button
                  className="user-menu-trigger"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-label="User menu"
                  aria-expanded={showUserMenu}
                >
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={displayName}
                      className="user-avatar"
                    />
                  ) : (
                    <div className="user-avatar user-avatar-placeholder">
                      {avatarLetter}
                    </div>
                  )}
                  <span className="user-name">{displayName}</span>
                </button>

                {showUserMenu && (
                  <div className="user-menu-dropdown">
                    <div className="user-menu-header">
                      <span className="user-menu-email">{user?.email}</span>
                      {profile?.role === 'admin' && (
                        <span className="user-role-badge">Admin</span>
                      )}
                    </div>

                    <div className="user-menu-divider" />
                    <button
                      type="button"
                      className="user-menu-item"
                      onClick={handleSignOut}
                    >
                      <HiLogout />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              !initializing && (
                <div className="auth-buttons">
                  <button
                    className="nav-link auth-login-btn"
                    onClick={handleLoginClick}
                  >
                    <HiUser />
                    <span>Login</span>
                  </button>
                  <button
                    className="btn btn-primary btn-sm auth-signup-btn"
                    onClick={handleSignUpClick}
                  >
                    Sign Up
                  </button>
                </div>
              )
            )}
          </nav>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialTab={authModalTab}
      />
    </>
  )
}

export default Header

